/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PageProductSortimentEntryResponse } from '../models/page-product-sortiment-entry-response';
import { PageProductSortimentPageResponse } from '../models/page-product-sortiment-page-response';
import { ProductSortimentCreateRequest } from '../models/product-sortiment-create-request';
import { ProductSortimentCreateResponse } from '../models/product-sortiment-create-response';
import { ProductSortimentEntryUpdateRequest } from '../models/product-sortiment-entry-update-request';
import { ProductSortimentFilter } from '../models/product-sortiment-filter';

/**
 * Product sortiment resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductSortimentsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getProductSortimentEntries
   */
  static readonly GetProductSortimentEntriesPath =
    '/sortiments/products/{prodSortId}/entries';

  /**
   * Gets product sortiment entries.
   *
   * Finds and returns products sortiment entries of the specified element.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductSortimentEntries()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductSortimentEntries$Response(
    params: {
      prodSortId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductSortimentEntryResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSortimentsService.GetProductSortimentEntriesPath,
      'get'
    );
    if (params) {
      rb.path('prodSortId', params.prodSortId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductSortimentEntryResponse>;
        })
      );
  }

  /**
   * Gets product sortiment entries.
   *
   * Finds and returns products sortiment entries of the specified element.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductSortimentEntries$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductSortimentEntries(
    params: {
      prodSortId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageProductSortimentEntryResponse> {
    return this.getProductSortimentEntries$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductSortimentEntryResponse>) =>
          r.body as PageProductSortimentEntryResponse
      )
    );
  }

  /**
   * Path part for operation updateProductSortimentEntries
   */
  static readonly UpdateProductSortimentEntriesPath =
    '/sortiments/products/{prodSortId}/entries';

  /**
   * Updates product sortiment entries.
   *
   * Updates products sortiment entries.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateProductSortimentEntries()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductSortimentEntries$Response(
    params: {
      prodSortId: string;
      body: ProductSortimentEntryUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSortimentsService.UpdateProductSortimentEntriesPath,
      'put'
    );
    if (params) {
      rb.path('prodSortId', params.prodSortId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates product sortiment entries.
   *
   * Updates products sortiment entries.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateProductSortimentEntries$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductSortimentEntries(
    params: {
      prodSortId: string;
      body: ProductSortimentEntryUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateProductSortimentEntries$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation addProductToSortiment
   */
  static readonly AddProductToSortimentPath = '/sortiments/products';

  /**
   * Creates a new instance of product sortiment.
   *
   * Adds a new product to the merchants product sortiment if it has not been added before.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addProductToSortiment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addProductToSortiment$Response(
    params: {
      body: ProductSortimentCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ProductSortimentCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSortimentsService.AddProductToSortimentPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ProductSortimentCreateResponse>;
        })
      );
  }

  /**
   * Creates a new instance of product sortiment.
   *
   * Adds a new product to the merchants product sortiment if it has not been added before.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addProductToSortiment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addProductToSortiment(
    params: {
      body: ProductSortimentCreateRequest;
    },
    context?: HttpContext
  ): Observable<ProductSortimentCreateResponse> {
    return this.addProductToSortiment$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ProductSortimentCreateResponse>) =>
          r.body as ProductSortimentCreateResponse
      )
    );
  }

  /**
   * Path part for operation deleteProductsFromSortiment
   */
  static readonly DeleteProductsFromSortimentPath = '/sortiments/products';

  /**
   * Delete product sortiments.
   *
   * Deletes the given product sortiments
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteProductsFromSortiment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteProductsFromSortiment$Response(
    params: {
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSortimentsService.DeleteProductsFromSortimentPath,
      'delete'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete product sortiments.
   *
   * Deletes the given product sortiments
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteProductsFromSortiment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteProductsFromSortiment(
    params: {
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteProductsFromSortiment$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getProductSortiment
   */
  static readonly GetProductSortimentPath = '/sortiments/products/items';

  /**
   * Gets products sortiment.
   *
   * Finds and returns products sortiment of logged in merchant.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductSortiment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getProductSortiment$Response(
    params: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: ProductSortimentFilter;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductSortimentPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSortimentsService.GetProductSortimentPath,
      'post'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductSortimentPageResponse>;
        })
      );
  }

  /**
   * Gets products sortiment.
   *
   * Finds and returns products sortiment of logged in merchant.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductSortiment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  getProductSortiment(
    params: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
      body: ProductSortimentFilter;
    },
    context?: HttpContext
  ): Observable<PageProductSortimentPageResponse> {
    return this.getProductSortiment$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductSortimentPageResponse>) =>
          r.body as PageProductSortimentPageResponse
      )
    );
  }

  /**
   * Path part for operation deleteProductFromSortiment
   */
  static readonly DeleteProductFromSortimentPath =
    '/sortiments/products/{prodSortId}';

  /**
   * Deletes a product from the sortiment.
   *
   * Deletes a product from the merchants product sortiment.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteProductFromSortiment()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteProductFromSortiment$Response(
    params: {
      prodSortId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductSortimentsService.DeleteProductFromSortimentPath,
      'delete'
    );
    if (params) {
      rb.path('prodSortId', params.prodSortId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Deletes a product from the sortiment.
   *
   * Deletes a product from the merchants product sortiment.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteProductFromSortiment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteProductFromSortiment(
    params: {
      prodSortId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteProductFromSortiment$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }
}
